import marketplacePropertiesService from './MarketplacePropertiesService';

export const A11yForIIBScreenReaderIteration = {
    v1: 1,
    v2: 2,
    v3: 3,
    v4: 4,
};

const a11yIIBScreenReaderService = {
    /**
     * Checks if Accessibility iteration for IIB Screen Reader is supported at or above the specified level.
     * @param {number} level - The level to check (defaults to 1).
     * @returns {boolean}
     */
    isA11yIterationForIIBScreenReaderSupported(level = A11yForIIBScreenReaderIteration.v1) {
        return marketplacePropertiesService.getA11yIterationForIIBScreenReader() >= level;
    },

    /**
    * Returns the tab index value based on whether the IIB Screen Reader accessibility feature is enabled.
    * @param {number|undefined} originalValue - The original pre-existing tab index value.
    * @param {number|undefined} newValue - The tab index value to use when accessibility mode is enabled.
    * @returns {number|undefined} The appropriate tab index value based on the accessibility setting.
    */
    getTabIndexValue(originalValue, newValue = 0) {
        const isA11yIterationForIIBScreenReaderEnabled = this.isA11yIterationForIIBScreenReaderSupported();
        return isA11yIterationForIIBScreenReaderEnabled ? newValue : originalValue;
    },

    /**
     * Returns the appropriate aria-label value based on whether the IIB Screen Reader accessibility feature is enabled.
     * @param {string|undefined} originalValue - The original aria-label value.
     * @param {string} newValue - The aria-label value to use when accessibility mode is enabled.
     * @returns {string} The appropriate aria-label value based on the accessibility setting.
     */
    getAriaLabelValue(originalValue, newValue) {
        const isA11yIterationForIIBScreenReaderEnabled = this.isA11yIterationForIIBScreenReaderSupported();
        return isA11yIterationForIIBScreenReaderEnabled ? newValue : originalValue;
    },

    /**
     * Returns the appropriate role value based on whether the IIB Screen Reader accessibility feature is enabled.
     * @param {string|undefined} originalValue - The original role value.
     * @param {string} newValue - The role value to use when accessibility mode is enabled.
     * @returns {string} The appropriate role value based on the accessibility setting.
     */
    getRoleValue(originalValue, newValue) {
        const isA11yIterationForIIBScreenReaderEnabled = this.isA11yIterationForIIBScreenReaderSupported();
        return isA11yIterationForIIBScreenReaderEnabled ? newValue : originalValue;
    },
};

/**
 * Returns a11y attributes if IIB Screen Reader V2 or higher is supported, otherwise returns empty object.
 * This centralizes the feature flag check for accessibility attributes.
 *
 * @param {Object} attrs - The accessibility attributes to conditionally apply
 * @returns {Object} The attributes if v2 or higher is supported (falls back to empty object for falsy values),
 *                   otherwise empty object. Always safe to spread in JSX.
 */
export const getA11yAttributesV2 = (attrs) => {
    const isV2Supported = a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported(
        A11yForIIBScreenReaderIteration.v2
    );
    return isV2Supported ? (attrs || {}) : {};
};

export default a11yIIBScreenReaderService;

